"use client";

import { LegalPageLayout } from "@/components/legal/legal-page-layout";
import { useTranslations } from "@/hooks/use-translations";

export default function ChildSafetyPage() {
  const { t } = useTranslations("legal");
  
  return (
    <LegalPageLayout title="Child Safety Standards (CSAE)" lastUpdated="May 2026">
      <div className="prose prose-neutral max-w-none space-y-6 text-neutral-700">
        <div className="rounded-lg border border-amber-200 bg-amber-50/60 p-4 mb-6">
          <p className="text-sm text-amber-900">
            <strong>{t("governingLanguage")}</strong>
          </p>
        </div>
        
        <section>
          <div className="rounded-lg border-2 border-red-200 bg-red-50/60 p-6 mb-6">
            <h2 className="gs-h2 text-red-900 mb-3">Zero Tolerance Policy</h2>
            <p className="text-base text-red-900 font-semibold">
              GrowthSync has a zero tolerance policy for child sexual abuse and exploitation material (CSAM/CSAE). 
              We strictly prohibit any content, behavior, or activity that involves, depicts, promotes, or facilitates 
              the sexual abuse or exploitation of minors. Any violation of this policy will result in immediate 
              account termination, removal of content, and reporting to law enforcement authorities.
            </p>
          </div>
        </section>

        <section>
          <h2 className="gs-h2 text-neutral-900 mt-8 mb-4">1. Prohibited Content and Behavior</h2>
          <p>
            The following content and behaviors are strictly prohibited on GrowthSync:
          </p>
          <ul className="list-disc pl-6 space-y-2 mt-4">
            <li><strong>Child Sexual Abuse Material (CSAM):</strong> Any visual, written, or audio content that depicts, describes, or promotes the sexual abuse or exploitation of minors.</li>
            <li><strong>Grooming:</strong> Any attempt to establish an emotional connection with a minor for the purpose of sexual abuse, exploitation, or trafficking.</li>
            <li><strong>Sexual Content Involving Minors:</strong> Any sexualized content, discussions, or depictions involving individuals under the age of 18.</li>
            <li><strong>Solicitation:</strong> Any attempt to solicit, recruit, or coerce minors for sexual purposes or exploitation.</li>
            <li><strong>Exploitation:</strong> Any activity that exploits minors, including but not limited to trafficking, forced labor, or commercial sexual exploitation.</li>
            <li><strong>Sharing or Distribution:</strong> Sharing, distributing, or attempting to share any prohibited content related to child exploitation.</li>
          </ul>
        </section>

        <section>
          <h2 className="gs-h2 text-neutral-900 mt-8 mb-4">2. Reporting Violations</h2>
          <p>
            If you encounter any content or behavior that violates our Child Safety Standards, please report it immediately. 
            We take all reports seriously and investigate them promptly.
          </p>
          
          <h3 className="gs-h3 text-neutral-900 mt-6 mb-3">In-App Reporting</h3>
          <p>
            You can report users or content directly within the GrowthSync app:
          </p>
          <ul className="list-disc pl-6 space-y-2 mt-4">
            <li>Navigate to the user's profile or the content in question</li>
            <li>Use the "Report" or "Block" feature available in the app</li>
            <li>Select the appropriate reason for your report (including "Child Safety" or "Inappropriate Content")</li>
            <li>Provide any additional details that may assist our investigation</li>
          </ul>

          <h3 className="gs-h3 text-neutral-900 mt-6 mb-3">Email Reporting</h3>
          <p>
            For urgent matters or if you need to report multiple violations, please contact us directly:
          </p>
          <p className="mt-2">
            <strong>Email:</strong> <a href="mailto:contact@growth-sync.com" className="text-blue-600 hover:text-blue-700 hover:underline">contact@growth-sync.com</a>
          </p>
          <p className="mt-2">
            Please include as much detail as possible, including:
          </p>
          <ul className="list-disc pl-6 space-y-2 mt-2">
            <li>The username or profile link of the reported user</li>
            <li>Description of the violation</li>
            <li>Screenshots or evidence (if available and safe to share)</li>
            <li>Date and time of the incident</li>
          </ul>
        </section>

        <section>
          <h2 className="gs-h2 text-neutral-900 mt-8 mb-4">3. Enforcement Actions</h2>
          <p>
            When violations are detected or reported, GrowthSync will take immediate action:
          </p>
          <ul className="list-disc pl-6 space-y-2 mt-4">
            <li><strong>Content Removal:</strong> All prohibited content will be immediately removed from the platform.</li>
            <li><strong>Account Suspension:</strong> Accounts found to be in violation will be immediately suspended pending investigation.</li>
            <li><strong>Permanent Ban:</strong> Accounts that have violated our Child Safety Standards will be permanently banned from the platform.</li>
            <li><strong>Law Enforcement Escalation:</strong> For severe violations involving CSAM or child exploitation, we will immediately report the matter to relevant law enforcement authorities, including but not limited to the National Center for Missing & Exploited Children (NCMEC) and local law enforcement agencies.</li>
            <li><strong>Data Preservation:</strong> We preserve all relevant data and evidence to assist law enforcement investigations.</li>
          </ul>
        </section>

        <section>
          <h2 className="gs-h2 text-neutral-900 mt-8 mb-4">4. Cooperation with Law Enforcement</h2>
          <p>
            GrowthSync is committed to cooperating fully with law enforcement agencies in the investigation and prosecution 
            of child sexual abuse and exploitation cases. We:
          </p>
          <ul className="list-disc pl-6 space-y-2 mt-4">
            <li>Respond promptly to valid legal requests and subpoenas from law enforcement</li>
            <li>Preserve relevant data and evidence as required by law</li>
            <li>Report suspected CSAM and child exploitation to appropriate authorities</li>
            <li>Work with international law enforcement agencies as needed</li>
            <li>Comply with all applicable laws and regulations regarding child safety</li>
          </ul>
        </section>

        <section>
          <h2 className="gs-h2 text-neutral-900 mt-8 mb-4">5. Age Guidance</h2>
          <p>
            GrowthSync is intended for users who are 18 years of age or older. Our platform is not directed to children 
            under the age of 18, and we do not knowingly collect personal information from minors.
          </p>
          <p className="mt-4">
            By using GrowthSync, you represent and warrant that you are at least 18 years old. If we become aware that 
            we have collected information from a user under 18, we will take immediate steps to delete that information 
            and terminate the account.
          </p>
          <p className="mt-4">
            If you are a parent or guardian and believe your child has provided us with personal information, please 
            contact us immediately at <a href="mailto:contact@growth-sync.com" className="text-blue-600 hover:text-blue-700 hover:underline">contact@growth-sync.com</a>.
          </p>
        </section>

        <section>
          <h2 className="gs-h2 text-neutral-900 mt-8 mb-4">6. Response Timeframe</h2>
          <p>
            We review all reports as quickly as possible, with priority given to reports involving child safety concerns. 
            Our response times are as follows:
          </p>
          <ul className="list-disc pl-6 space-y-2 mt-4">
            <li><strong>Urgent Child Safety Reports:</strong> Reviewed immediately, typically within hours of receipt</li>
            <li><strong>Standard Reports:</strong> Reviewed within 24-48 hours</li>
            <li><strong>Law Enforcement Requests:</strong> Processed in accordance with legal requirements and as quickly as possible</li>
          </ul>
          <p className="mt-4">
            Please note that complex investigations may require additional time, but we will keep you informed of our 
            progress when appropriate.
          </p>
        </section>

        <section>
          <h2 className="gs-h2 text-neutral-900 mt-8 mb-4">7. Prevention Measures</h2>
          <p>
            GrowthSync employs multiple measures to prevent and detect child safety violations:
          </p>
          <ul className="list-disc pl-6 space-y-2 mt-4">
            <li>Automated content moderation systems to detect prohibited content</li>
            <li>Age verification requirements during account registration</li>
            <li>User reporting mechanisms for community-driven safety</li>
            <li>Regular monitoring and review of platform content</li>
            <li>Partnerships with safety organizations and law enforcement</li>
            <li>Ongoing training for our moderation team on child safety issues</li>
          </ul>
        </section>

        <section>
          <h2 className="gs-h2 text-neutral-900 mt-8 mb-4">8. Contact Information</h2>
          <p>
            For questions, concerns, or reports related to child safety, please contact us:
          </p>
          <p className="mt-4">
            <strong>Email:</strong> <a href="mailto:contact@growth-sync.com" className="text-blue-600 hover:text-blue-700 hover:underline">contact@growth-sync.com</a>
          </p>
          <p className="mt-2">
            <strong>Company:</strong> GrowthSync Network Ltd<br />
            <strong>Registered Office:</strong> Unit 6, Balfour Court, Leyland, PR25 2TF, United Kingdom<br />
            <strong>Company No.:</strong> 16795645
          </p>
          <p className="mt-4">
            For urgent child safety matters, please mark your email as "URGENT - Child Safety" in the subject line 
            to ensure immediate attention.
          </p>
        </section>

        <section>
          <h2 className="gs-h2 text-neutral-900 mt-8 mb-4">9. Additional Resources</h2>
          <p>
            If you or someone you know is in immediate danger, please contact your local emergency services immediately.
          </p>
          <p className="mt-4">
            For additional support and resources related to child safety, please consider contacting:
          </p>
          <ul className="list-disc pl-6 space-y-2 mt-4">
            <li><strong>National Center for Missing & Exploited Children (NCMEC):</strong> <a href="https://www.missingkids.org" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-700 hover:underline">www.missingkids.org</a></li>
            <li><strong>Childhelp National Child Abuse Hotline:</strong> 1-800-4-A-CHILD (1-800-422-4453)</li>
            <li><strong>Your local law enforcement agency</strong></li>
          </ul>
        </section>

        <section>
          <h2 className="gs-h2 text-neutral-900 mt-8 mb-4">10. Updates to This Policy</h2>
          <p>
            We may update this Child Safety Standards policy periodically to reflect changes in our practices, 
            legal requirements, or industry standards. The latest version will always be available at{" "}
            <a href="/child-safety" className="text-blue-600 hover:text-blue-700 hover:underline">https://growth-sync.com/child-safety</a>.
          </p>
          <p className="mt-4">
            Users will be notified of any significant changes to this policy via the app or email.
          </p>
        </section>
      </div>
    </LegalPageLayout>
  );
}
